import type { ComponentType } from 'react';

export interface BlogPostFrontmatter {
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  /**
   * An unpublished draft: kept out of the blog index, the public route
   * registry, the sitemap and the prerendered output.
   */
  draft?: boolean;
}

export interface BlogPost {
  frontmatter: BlogPostFrontmatter;
  Component: ComponentType;
}

const modules = import.meta.glob<{
  default: ComponentType;
  frontmatter: BlogPostFrontmatter;
}>('/src/content/blog/*.mdx', { eager: true });

const allPosts: BlogPost[] = Object.values(modules)
  .map((mod) => ({ frontmatter: mod.frontmatter, Component: mod.default }))
  // Slug breaks ties so posts sharing a date keep a stable order, matching the
  // ordering scripts/seo-artifacts.ts uses for the sitemap.
  .sort(
    (a, b) =>
      b.frontmatter.date.localeCompare(a.frontmatter.date) ||
      a.frontmatter.slug.localeCompare(b.frontmatter.slug),
  );

/** Published posts. The blog index, the route registry and the sitemap use only these. */
export const blogPosts: BlogPost[] = allPosts.filter((post) => !post.frontmatter.draft);

export function getPostBySlug(slug: string | undefined): BlogPost | undefined {
  // Drafts resolve while developing so an author can preview one before
  // publishing; a production build has no route for them at all.
  const searchable = import.meta.env.DEV ? allPosts : blogPosts;
  return searchable.find((post) => post.frontmatter.slug === slug);
}
